import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ErrorBoundary — catches render errors so the whole app doesn't go blank.
 * Wrap around any page or panel that could throw.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Log to console for debugging — remove / replace with Sentry in production
    console.error('[Wohler ErrorBoundary]', error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  render() {
    if (this.state.hasError) {
      const { fallback, compact } = this.props;

      // If caller provided a custom fallback UI, use it
      if (fallback) return fallback;

      if (compact) {
        return (
          <div style={{
            padding: '16px 20px',
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8,
            color: '#f87171',
            fontSize: 13,
            margin: '8px 0',
          }}>
            ⚠️ This section couldn't render.{' '}
            <button
              onClick={this.handleReset}
              style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
            >
              Try again
            </button>
          </div>
        );
      }

      return (
        <div style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚗️</div>
          <h2 style={{ color: '#f87171', marginBottom: 8, fontSize: 22 }}>
            Something went wrong rendering this page
          </h2>
          <p style={{ color: '#94a3b8', maxWidth: 480, marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
            A data field on this compound or category couldn't be displayed.
            This is usually caused by a field containing unexpected data (e.g., an object where text was expected).
          </p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 8,
              padding: '12px 16px',
              color: '#fca5a5',
              fontSize: 12,
              maxWidth: 640,
              overflowX: 'auto',
              textAlign: 'left',
              marginBottom: 24,
            }}>
              {this.state.error.toString()}
              {this.state.info?.componentStack}
            </pre>
          )}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 20px',
                background: 'rgba(99,102,241,0.15)',
                border: '1px solid rgba(99,102,241,0.35)',
                borderRadius: 8,
                color: '#a5b4fc',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              ↻ Try Again
            </button>
            <Link
              to="/categories"
              style={{
                padding: '10px 20px',
                background: 'rgba(16,185,129,0.1)',
                border: '1px solid rgba(16,185,129,0.25)',
                borderRadius: 8,
                color: '#34d399',
                textDecoration: 'none',
                fontSize: 14,
              }}
            >
              ← Back to Categories
            </Link>
            <Link
              to="/"
              style={{
                padding: '10px 20px',
                background: 'rgba(100,116,139,0.1)',
                border: '1px solid rgba(100,116,139,0.25)',
                borderRadius: 8,
                color: '#94a3b8',
                textDecoration: 'none',
                fontSize: 14,
              }}
            >
              🏠 Home
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
